import { render, screen, waitFor } from '@testing-library/preact';
import { expect } from 'chai';
import { VisProgressiveLoader } from '../';
import { matchTranslationBundle } from '../../utils/UNSAFE_matchTranslationBundle';
import { RootEnvironmentProvider } from '../../UNSAFE_Environment';
import type { RootEnvironment } from '../../UNSAFE_Environment';

const ariaLabel = 'ariaLabel';
const ariaDescribeId = 'ariaDescribeId';
const ariaLabelledId = 'ariaLabelledId';
const componentText = 'Dummy Component';

describe('Test VisProgressiveLoader component', () => {
  it('render (loading)', async function () {
    await setupVisProgressiveLoader(true);
    // There's a delay before loading indicator is shown
    await waitFor(() => {
      // The dummy component doesn't have role application
      // During loading, the container should have role application
      const target = screen.getByRole('application');
      expect(target).not.null;
    });
  });

  it('live region text (loading)', async function () {
    const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
    const { default: translations } = await import(`../../resources/nls/${locale}/bundle.ts`);

    await setupVisProgressiveLoader(true);
    // There's a delay before loading indicator is shown
    await waitFor(() => {
      // The dummy component doesn't have role application
      // During loading, the container should have role application
      const target = screen.getByRole('application');
      expect(target).not.null;
    });
    await waitFor(() => {
      const loadingText = screen.getByText(translations.vis_stateLoading());
      expect(loadingText).not.null;
    });
  });

  it('render (loaded)', async function () {
    await setupVisProgressiveLoader(false);
    const compText = await screen.findByText(componentText);
    expect(compText).not.null;
  });

  it('live region text (loaded)', async function () {
    const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
    const { default: translations } = await import(`../../resources/nls/${locale}/bundle.ts`);

    await setupVisProgressiveLoader(false);
    const loadedText = await screen.findByText(translations.vis_stateLoaded());
    expect(loadedText).not.null;
  });

  it('aria-label, aria-labelledby and aria-describedby is set', async function () {
    await setupVisProgressiveLoader(true, ariaDescribeId, ariaLabelledId, ariaLabel);
    await waitFor(() => {
      const target = screen.getByRole('application');
      expect(target?.getAttribute('aria-label')).equal(ariaLabel);
    });
    const target = screen.getByRole('application');
    expect(target?.getAttribute('aria-labelledby')).equal(ariaLabelledId);
    expect(target?.getAttribute('aria-describedby')).contains(ariaDescribeId);
  });
});

const setupVisProgressiveLoader = async (
  isLoading?: boolean,
  describedBy?: string,
  labelledBy?: string,
  label?: string
) => {
  const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
  const { default: translations } = await import(`../../resources/nls/${locale}/bundle.ts`);
  const env: Partial<RootEnvironment> = {
    translations: { '@oracle/oraclejet-preact': translations }
  };
  const visProgressiveLoader = (
    <VisProgressiveLoader
      role="application"
      type="bar"
      isLoading={isLoading ?? true}
      width="100x"
      height="100x"
      aria-label={label}
      aria-describedby={describedBy}
      aria-labelledby={labelledBy}>
      <div style={{ width: '100%', height: '100%' }}>{componentText}</div>
    </VisProgressiveLoader>
  );

  return render(
    <RootEnvironmentProvider environment={env}>{visProgressiveLoader}</RootEnvironmentProvider>
  );
};
